import {merge, Observable} from 'rxjs';
import {delay, filter, map, scan} from 'rxjs/operators';
import {orderUtil} from '@/helper/order';
import {uiMsg$} from '@/ui';
import {wsLogout$} from '@/user/auth-ws';
import {contractSnapshotOrders$, contractIncrementalOrders$} from '@/orders-contract/orders-contract';
import {wsOpened$} from '@/ws';
import type {ws} from '@/types';
type ContractOrderRaw = ws.ContractOrderRaw;

export type Cache = Map<string, ContractOrderRaw>;
type Action = (map: Cache) => Cache;

const resetAction$ = merge(wsLogout$, wsOpened$).pipe(
  map(
    (): Action => (cache: Cache) => {
      cache.clear();
      return cache;
    }
  )
);

export interface FUOrderConditionRemove {
  type: 'order-condition-remove';
  orderID: string;
}
const removeByUserAction$ = uiMsg$.pipe(
  filter((o) => o.type === 'order-condition-remove'),
  map<FUOrderConditionRemove, Action>((data) => (cache: Cache) => {
    cache.delete(data.orderID);
    return cache;
  })
);

const snapshotOrders$ = contractSnapshotOrders$.pipe(map(({orders: {open}}) => open));
const snapshotAction$ = createSnapshotAction$(snapshotOrders$);

const incrementalActiveOrders$ = contractIncrementalOrders$.pipe(
  map(({orders: {open, closed}}) => [...closed, ...open])
);
const updateAction$ = createUpdateAction$(incrementalActiveOrders$);

const incrementalClosedOrders$ = contractIncrementalOrders$.pipe(map(({orders: {closed}}) => closed));
const removeAction$ = createRemoveAction$(incrementalClosedOrders$);

export const contractConditionalOrdersCache$ = merge<[Action, Action, Action, Action, Action]>(
  snapshotAction$,
  updateAction$,
  removeAction$,
  resetAction$,
  removeByUserAction$
).pipe(scan((p, c) => c(p), new Map<string, ContractOrderRaw>()));

export function createSnapshotAction$(snapshotOrders$: Observable<ContractOrderRaw[]>) {
  return snapshotOrders$.pipe(
    map((orders) => orders.filter((order) => isDisplayOrder(order, 'snapshot'))),
    filter((orders) => orders.length > 0),
    map(convertSnapshotAction)
  );
}

export function createUpdateAction$(incrementalOrders$: Observable<ContractOrderRaw[]>) {
  return incrementalOrders$.pipe(
    map((orders) => orders.filter((order) => isDisplayOrder(order, 'incremental'))),
    filter((orders) => orders.length > 0),
    map(convertUpdateAction)
  );
}

export function createRemoveAction$(incrementalOrders$: Observable<ContractOrderRaw[]>) {
  return incrementalOrders$.pipe(
    map((orders) => orders.filter((order) => isDisplayOrder(order, 'incremental'))),
    filter((orders) => orders.length > 0),
    map(convertRemoveAction),
    delay(5000)
  );
}

function convertSnapshotAction(orders): Action {
  return function (cache: Cache) {
    orders.forEach((o) => cache.set(o.orderID, o));
    return cache;
  };
}

function convertUpdateAction(orders): Action {
  return function (cache: Cache) {
    orders.forEach((o) => cache.set(o.orderID, o));

    // remove rejected orders immediately
    for (let order of cache.values()) {
      if (order.ordStatus === 'Rejected') {
        cache.delete(order.orderID);
      }
    }

    return cache;
  };
}

function convertRemoveAction(orders): Action {
  return function (cache: Cache) {
    orders.forEach((o) => cache.delete(o.orderID));
    return cache;
  };
}

function isDisplayOrder(raw: ContractOrderRaw, type: string) {
  // 先决条件
  const acceptable =
    ['New', 'Replace', 'Cancel'].indexOf(raw.action) > -1 &&
    ['ReplaceRejected', 'CancelRejected'].indexOf(raw.execStatus) < 0;
  if (!acceptable) {
    return false;
  }

  if (orderUtil.isBracketOrders(raw.ordType)) {
    return false;
  }

  if (!orderUtil.isConditionalOrder(raw.ordType)) {
    return false;
  }

  if (type === 'snapshot') {
    // 已结束的订单不显示
    return ['New', 'PartiallyFilled', 'Untriggered'].indexOf(raw.ordStatus) > -1;
  }

  if (type === 'incremental') {
    return (
      ['New', 'PartiallyFilled', 'Untriggered', 'Filled', 'Deactivated', 'Canceled', 'Rejected', 'Triggered'].indexOf(
        // Triggered 订单中间态，一般下发到前端，只有订单异常才有可能推送这个订单状态到前端
        raw.ordStatus
      ) > -1
    );
  }

  return false;
}
